import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AppConfig } from 'app/app.config';
import { TagItem } from 'app/components/common/tag/tag.component';
import { AuthService } from 'app/services/auth.service';
import { Clock } from 'app/services/clock';
import { ConfigService } from 'app/services/config.service';
import { DeviceRepository } from 'app/services/database/device.repository';
import { DevicesGroupRepository } from 'app/services/database/devicesGroup.repository';
import { PresentationRepository } from 'app/services/database/presentation.repository';
import { RoleRepository } from 'app/services/database/role.repository';
import { UserRepository } from 'app/services/database/user.repository';
import { Logger } from 'app/shared/logger';
import { LoggerFactory } from 'app/shared/loggerFactory';
import { DeviceModel } from 'app/views/devices/models/device.model';
import { ScheduleModel } from 'app/views/devices/models/schedule.model';
import * as url from 'url';

export enum DeviceStatus {
    Unknown,
    Initializing,
    WaitingForPresentation,
    LoadingPresentation,
    Unreachable,
    Presenting,
    Shutdown
}

@Injectable()
export class DeviceService {
    private readonly linkDeviceUrl = '/device/linkDevice/:pin';
    private readonly validatePinUrl = '/device/verifyPin/:pin';
    private readonly nameDeviceUrl = '/device/nameDevice/:did';
    private readonly createCustomTokenForWebViewUrl = '/device/createCustomTokenForWebDevice';

    private logger: Logger;

    constructor(
        private http: HttpClient,
        private appConfig: AppConfig,
        private deviceRepository: DeviceRepository,
        private userRepository: UserRepository,
        private roleRepository: RoleRepository,
        private presentationRepository: PresentationRepository,
        private devicesGroupRepository: DevicesGroupRepository,
        private auth: AuthService,
        private translate: TranslateService,
        private clock: Clock,
        private loggerFactory: LoggerFactory,
        private config: ConfigService
    ) {
        this.logger = loggerFactory.create('DeviceService');
    }

    public async deviceExists(deviceId: string): Promise<boolean> {
        return await this.deviceRepository.exists(deviceId);
    }

    public async getDevice(id: string): Promise<DeviceModel> {
        let device = await this.deviceRepository.get(id);
        return device;
    }

    public async getUserDeviceList(userId: string): Promise<DeviceModel[]> {
        let user = await this.userRepository.get(userId);
        let devices: DeviceModel[] = [];
        if (user.devices && user.devices.length !== 0) {
            for (let deviceId of user.devices) {
                try {
                    let device = await this.deviceRepository.get(deviceId);
                    devices.push(device);
                } catch (err) {
                    this.logger.error(`An error occured while getting the user '${userId}' device '${deviceId}'.`);
                }
            }

            devices.sort((a: DeviceModel, b: DeviceModel) => {
                return b.modificationDate - a.modificationDate;
            });
        }
        return devices;
    }

    private async getDeviceStatus(device: DeviceModel): Promise<DeviceStatus> {

        // Check if the device is in an unknown state
        if (!(device.metadata && device.metadata.state)) {
            return DeviceStatus.Unknown;
        }

        let metadataUpdateDelay = await this.config.get('metadataUpdateDelay', 1000 * 60 * 5);
        let metadataValidityCycles = await this.config.get('metadataValidityCycles', 3);
        let lastUpdate = device.metadata.stateUpdateTime;

        let unreachableMaxTime = device.metadata.stateUpdateTime + (metadataValidityCycles * metadataUpdateDelay);

        // Check if the device is considered as unreachable
        if (unreachableMaxTime < Date.now()) return DeviceStatus.Unreachable;

        switch (device.metadata.state) {
            case 'init':
                return DeviceStatus.Initializing;
            case 'preload':
                if (device.metadata.currentPresentationId) {
                    return DeviceStatus.LoadingPresentation;
                } else {
                    return DeviceStatus.WaitingForPresentation;
                }
            case 'show':
                return DeviceStatus.Presenting;
            default:
                return DeviceStatus.Unknown;
        }
    }

    public async createWebView(): Promise<{device: DeviceModel, customToken: string}> {
        const info = await this.http.get<{
            customToken: string,
            deviceId: string
        }>(this.appConfig.getApiUrl(this.createCustomTokenForWebViewUrl)).toPromise();

        const device = await this.getDevice(info.deviceId);
        return {
            device,
            customToken: info.customToken
        };
    }

    public async createDeviceTag(device: DeviceModel, showLabel: boolean = true): Promise<TagItem> {

        // Get the device computed status
        let status = await this.getDeviceStatus(device);
        let tagLabel = showLabel ? await this.translate.get('modules.devices.states.state').toPromise() : undefined;
        let statusText = await this.translate.get('modules.devices.states.' + DeviceStatus[status]).toPromise();
        let stateLevel;

        switch (status) {
            case DeviceStatus.Initializing:
            case DeviceStatus.LoadingPresentation:
                stateLevel = 'warning';
                break;
            case DeviceStatus.Presenting:
                stateLevel = 'info';
                break;
            case DeviceStatus.WaitingForPresentation:
                stateLevel = 'default';
                break;
            case DeviceStatus.Unknown:
            case DeviceStatus.Unreachable:
            case DeviceStatus.Shutdown:
                stateLevel = 'danger';
                break;
        }

        return new TagItem(tagLabel, statusText, stateLevel);
    }

    public async saveDevice(deviceModel: DeviceModel) {
        if (deviceModel.name === undefined || deviceModel.name === '') {
            throw new Error('Cannot save a device without a name');
        }
        if (deviceModel.owners === undefined) {
            throw new Error('Cannot save a device without owners');
        }
        if (deviceModel.creationDate === undefined) {
            throw new Error('Cannot save a device without creation date');
        }

        if (!deviceModel.id) {
            let id = await this.deviceRepository.generateKey();
            deviceModel.id = id;
        }

        if (deviceModel.id === undefined) {
            throw new Error('Cannot save a device without id');
        }

        deviceModel.modificationDate = this.clock.now;

        await this.addDeviceToUser(deviceModel);
        // Set owner of group
        await this.addOwnerToDevicesGroup(deviceModel.groupId, deviceModel);
        // Set owner of his schedule
        await this.setDeviceOwnerOfSchedule(deviceModel.schedule, deviceModel.id);
        // Set owner of group schedule
        await this.setDeviceOwnerOfGroupSchedule(deviceModel);

        try {
            await this.deviceRepository.set(deviceModel.id, deviceModel);
        } catch (err) {
            throw err;
        }
    }

    private async setDeviceOwnerOfGroupSchedule(deviceModel: DeviceModel) {
        if (deviceModel.groupId) {
            let deviceGroup = await this.devicesGroupRepository.get(deviceModel.groupId);
            await this.setDeviceOwnerOfSchedule(deviceGroup.schedule, deviceModel.id);
        }
    }

    private async setDeviceOwnerOfSchedule(schedule: ScheduleModel, deviceId: string) {
        if (!schedule) return;
        if (schedule.defaultPresentationId) {
            await this.addDeviceToPresentationOwners(schedule.defaultPresentationId, deviceId);
        }
        if (schedule.entries) {
            for (let scheduledPres of schedule.entries) {
                await this.addDeviceToPresentationOwners(scheduledPres.presentationId, deviceId);
            }
        }
    }

    private async addDeviceToPresentationOwners(presentationId: string, deviceId: string) {
        let presentation = await this.presentationRepository.get(presentationId);

        if (!presentation.owners) {
            presentation.owners = {};
        }

        // Set the device as an owner of the presentation
        presentation.owners[deviceId] = true;

        // Update the presentation
        await this.presentationRepository.update(presentation.id, presentation);
    }

    private async addDeviceToUser(deviceModel: DeviceModel) {
        let authUser = await this.auth.getCurrentUser();
        let userModel = await this.userRepository.get(authUser.uid);
        let isAlreadyAssigned = false;
        if (!userModel.devices) {
            userModel.devices = [];
        } else {
            for (let id of userModel.devices) {
                if (id === deviceModel.id) {
                    isAlreadyAssigned = true;
                }
            }
        }
        if (!isAlreadyAssigned) {
            userModel.devices.push(deviceModel.id);
            await this.userRepository.update(authUser.uid, userModel);
        }
    }

    private async removeDeviceFromUser(deviceId: string) {
        let authUser = await this.auth.getCurrentUser();
        let userModel = await this.userRepository.get(authUser.uid);
        if (userModel.devices) {
            userModel.devices = userModel.devices.filter(obj => obj !== deviceId);
            await this.userRepository.set(authUser.uid, userModel);
        }
    }

    public validatePin(pin: string) {
        return new Promise((resolve, reject) => {
            this.http.get(url.resolve(this.appConfig.getApiUrl(this.validatePinUrl), pin)).subscribe((res: any) => {
                resolve(res.isValid);
            });
        });
    }

    public linkDevice(pin: string) {
        return new Promise<string>((resolve, reject) => {
            this.http.get(url.resolve(this.appConfig.getApiUrl(this.linkDeviceUrl), pin)).subscribe((res: any) => {
                resolve(res.deviceId);
            });
        });
    }

    public async unlinkDevice(deviceId: string) {
        let device = await this.deviceRepository.get(deviceId);
        let user = await this.auth.getCurrentUser();
        let ownerUser = await this.userRepository.get(user.uid);
        ownerUser.devices.splice(ownerUser.devices.indexOf(deviceId), 1);
        this.userRepository.update(user.uid, ownerUser);
        try {
            this.deviceRepository.remove(deviceId);
        } catch (e) {
            this.logger.error(`Failed to remove device '${deviceId}' from database for user '${user.uid}'.`, e);
        }
        try {
            this.roleRepository.remove(deviceId);
        } catch (e) {
            this.logger.error(`Failed to remove device '${deviceId}' from role repository.`, e);
        }
    }

    public nameDevice(deviceId: string, name: string) {
        return new Promise<string>((resolve, reject) => {
            this.http.post(url.resolve(this.appConfig.getApiUrl(this.nameDeviceUrl), deviceId), { name }).subscribe((res: any) => {
                resolve(res.success);
            });
        });
    }

    private async addOwnerToDevicesGroup(deviceGroupId: string, device: DeviceModel) {
        if (device.groupId) {
            let devicesGroup = await this.devicesGroupRepository.get(deviceGroupId);

            if (!devicesGroup.owners) devicesGroup.owners = [];
            devicesGroup.owners[device.id] = true;

            await this.devicesGroupRepository.update(deviceGroupId, devicesGroup);
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/services/device.service.ts